<template>
  <div>
    <empty-resource-table v-if="!roles[0] && !loadingTable" resource="roles" />

    <a-table
      v-if="loadingTable || roles[0]"
      :columns="rolesColumns"
      :data-source="roles"
      :loading="loadingTable"
      :pagination="false"
      :expand-row-by-click="true"
      :scroll="{ x: 1000 }"
      :row-key="(role) => role.id"
      @change="(pagination, filters, sorter) => handleChange(pagination, filters, sorter)"
    >
      <template #user="name, r">
        <span class="d-flex align-items-center">
          <a-avatar :src="r.user.gravatar" class="min-width-avatar mr-2" />
          <router-link :to="{ name: 'user', params: { id: r.user.id } }" @click.native.stop>
            {{ name }}
          </router-link>
        </span>
      </template>

      <template #project="projectName, r">
        <span class="d-flex flex-column">
          <small>{{ r.project.client.name }}</small>
          <router-link
            :to="{ name: 'project', params: { projectId: r.project.id } }"
            @click.native.stop
          >
            {{ projectName }}
          </router-link>
        </span>
      </template>

      <template #roleTitle="title, r">
        <span>
          {{ title }}
          <a-tag v-if="r.reportable === false" :color="Colors.blue_darker" class="ml-2">
            TRANSFER ONLY
          </a-tag>
        </span>
      </template>

      <template #rate="rate">
        <span>{{ rate ? toMoney(rate) : "-" }}</span>
      </template>

      <template #workload="workload">
        <span class="d-flex flex-column">
          {{ toPercentage(workload) }}
        </span>
      </template>

      <template #workExperienceStatus="_count, r">
        <span class="d-flex align-items-center justify-content-center">
          <a-tag v-if="r.disable_cv" class="default-cursor">N/A</a-tag>
          <a-tag
            v-else-if="r.work_experiences_count > 0"
            :color="Colors.green"
            class="default-cursor"
            >PRESENT</a-tag
          >
          <a-tag v-else :color="Colors.orange" class="default-cursor">MISSING</a-tag>
        </span>
      </template>

      <template #extensionStatus="r">
        <span>
          <a-tag
            v-if="r.extension_status === 'no_pending'"
            :color="extensionStatusColorRef[r.extension_status]"
            class="default-cursor"
          >
            NO PENDING
          </a-tag>
          <a-tag
            v-if="r.extension_status === 'approved'"
            :color="extensionStatusColorRef[r.extension_status]"
            class="default-cursor"
          >
            APPROVED
          </a-tag>
          <a-tag
            v-if="r.extension_status === 'to_be_discussed'"
            :color="extensionStatusColorRef[r.extension_status]"
            class="default-cursor"
          >
            TO BE DISCUSSED
          </a-tag>
          <a-tag
            v-if="r.extension_status === 'waiting'"
            :color="extensionStatusColorRef[r.extension_status]"
            class="default-cursor"
          >
            WAITING
          </a-tag>
        </span>
      </template>

      <template #actions="r">
        <span>
          <role-actions
            :role="r"
            :profile="profile"
            @show-edit-role-modal="() => showEditRoleModal(r)"
            @show-delete-role-modal="() => showDeleteRoleModal(r)"
          />
        </span>
      </template>

      <template #expandedRowRender="r">
        <div>
          <a-row>
            <a-col :span="8">
              <strong class="mr-2">Project:</strong>
              <router-link :to="{ name: 'project', params: { projectId: r.project.id } }">
                {{ r.project.name }}
              </router-link>
            </a-col>

            <a-col :span="8">
              <strong class="mr-4">Hourly rate:</strong>
              {{ toMoney(r.hourly_rate) }}
            </a-col>

            <a-col :span="8">
              <strong class="mr-2">Start date:</strong>
              {{ r.start_date }}
            </a-col>
          </a-row>

          <a-row class="mt-1">
            <a-col :span="8">
              <strong class="mr-3">Client:</strong>
              <router-link :to="{ name: 'client', params: { id: r.project.client.id } }">
                {{ r.project.client.name }}
              </router-link>
            </a-col>

            <a-col :span="8">
              <strong class="mr-2">Project status:</strong>
              <a-tag
                :color="r.project.status === 'active' ? Colors.green : Colors.orange"
                class="default-cursor"
              >
                {{ r.project.status.toUpperCase() }}
              </a-tag>
            </a-col>

            <a-col :span="8">
              <strong class="mr-3">End date:</strong>
              {{ r.end_date }}
            </a-col>
          </a-row>

          <a-row v-if="r.reportable === false" class="mt-2">
            <a-col :span="22">
              <strong class="mr-2">Reporting status:</strong>
              <a-tag :color="Colors.blue_darker" class="default-cursor">TRANSFER ONLY</a-tag>
              <span class="ml-2">This role cannot be selected for new time reports.</span>
            </a-col>
          </a-row>

          <a-row v-if="!r.disable_cv && r.work_experiences && r.work_experiences[0]" class="mt-2">
            <a-col :span="22">
              <strong class="mr-2">Work Experiences:</strong>
              <span v-for="work_experience in r.work_experiences" :key="work_experience.id">
                <a-tag
                  class="default-cursor"
                  @click.stop="redirectToWorkExperiences(work_experience)"
                >
                  {{ `${work_experience.title} at ${work_experience.company_name}` }}
                </a-tag>
              </span>
            </a-col>
          </a-row>

          <a-row v-else-if="!r.disable_cv" class="mt-2">
            <a-col :span="22">
              <strong class="mr-2">Work Experiences:</strong>
              <a-button
                v-if="canAddWorkExperienceToRoleUser(r) && !r.disable_cv"
                type="primary"
                size="small"
                @click.stop="showAddWorkExperienceModal(r)"
              >
                Add Work Experience
              </a-button>
              <em v-else>No work experiences connected</em>
            </a-col>
          </a-row>

          <a-row class="mt-2">
            <a-col :span="22">
              <strong class="mr-2">Agreement Link:</strong>
              <span v-if="r.agreement_link">
                <a :href="`${r.agreement_link}`" target="_blank" rel="noopener noreferrer">
                  {{ r.agreement_link }}
                </a>
              </span>
              <em v-else>No agreement link available</em>
            </a-col>
          </a-row>

          <a-row>
            <a-col :span="20">
              <role-actions
                :role="r"
                :profile="profile"
                :with-title="true"
                @show-edit-role-modal="() => showEditRoleModal(r)"
                @show-delete-role-modal="() => showDeleteRoleModal(r)"
              />
            </a-col>
          </a-row>

          <a-row class="mt-2">
            <a-col :span="20">
              <latest-role-extension-overview
                :profile="profile"
                :acl="acl"
                :role="r"
                :show-create-button="true"
                @on-successful-change="$emit('refresh-role', r.id)"
              />
            </a-col>
          </a-row>
        </div>
      </template>
    </a-table>

    <a-pagination
      v-if="rolesPagination && (loadingTable || roles[0])"
      :current="currentPage"
      :total="rolesPagination.total"
      :show-size-changer="true"
      :page-size="pageSize"
      :page-size-options="['10', '25', '50', '100']"
      size="small"
      class="d-flex justify-content-end mt-2"
      @change="(page, newPageSize) => $emit('get-roles-page', { page, pageSize: newPageSize })"
      @showSizeChange="
        (pageNumber, newPageSize) =>
          $emit('get-roles-page', { page: pageNumber, pageSize: newPageSize })
      "
    />

    <!-- EDIT PROJECT ROLE MODAL -->
    <edit-project-role-modal
      :visible="editRoleModalVisible"
      :acl="acl"
      :cost-centers="costCenters"
      :role="role"
      :project="role ? role.project : undefined"
      :subprojects="subprojects"
      @close="editRoleModalVisible = false"
      @get-subprojects-page="(projectId) => $emit('get-subprojects-page', projectId)"
      @get-cost-centers="$emit('get-cost-centers')"
      @update-project-role="(updatedRole) => $emit('update-project-role', updatedRole)"
    />

    <!-- DELETE ROLE MODAL -->
    <delete-role-modal
      :visible="deleteRoleModalVisible"
      :role="role"
      @close="deleteRoleModalVisible = false"
      @destroy-role="(projectId, roleId) => $emit('destroy-user-role', projectId, roleId)"
    />

    <!-- ADD WORK EXPERIENCE TO ROLE MODAL -->
    <add-work-experience-to-role-modal
      :visible="addWorkExperienceModalVisible"
      :profile="profile"
      :skills="skills"
      :role="role"
      @work-experience-added="handleWorkExperienceAdded"
      @close="addWorkExperienceModalVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import EditProjectRoleModal from "../../projects/_components/EditProjectRoleModal.vue";
import { RolesFilterInterface, RoleInterface } from "../types";
import EmptyResourceTable from "@/modules/common/_components/EmptyResourceTable.vue";
import { BaseAclInterface, ProfileInterface } from "@/modules/authentication/types";
import DeleteRoleModal from "../../projects/_components/DeleteRoleModal.vue";
import AddWorkExperienceToRoleModal from "@/modules/work_experiences/_components/AddWorkExperienceToRoleModal.vue";
import LatestRoleExtensionOverview from "./LatestRoleExtensionOverview.vue";
import { extensionStatusColor } from "../_utils/role-extension";
import Colors from "@/assets/scss/_colors.module.scss";
import { PaginationInterface, TableColumnInterface } from "@/common/types";
import { SkillInterface } from "@/modules/skills/types";
import { rolesColumns as defaultRolesColumns } from "../_utils/tables";
import RoleActions from "./RoleActions.vue";
import useMixin from "@/useMixin";
import { ProjectInterface, NewSubprojectInterface } from "../../projects/types";

// Props
const props = defineProps({
  rolesPagination: { type: Object as () => PaginationInterface, default: undefined },
  profile: { type: Object as () => ProfileInterface, default: undefined },
  roles: { type: Array as () => Array<RoleInterface>, default: () => [] },
  skills: { type: Array as () => Array<SkillInterface>, default: () => [] },
  acl: { type: Object as () => BaseAclInterface, default: undefined },
  costCenters: { type: Array as () => Array<any>, default: () => [] },
  loadingTable: { type: Boolean, default: false },
  currentPage: { type: Number, default: 1 },
  pageSize: { type: Number, default: 25 },
  rolesColumns: {
    type: Array as () => Array<TableColumnInterface>,
    default: () => defaultRolesColumns,
  },
  subprojects: { type: Array as () => Array<NewSubprojectInterface>, default: () => [] },
  project: { type: Object as () => ProjectInterface, default: undefined },
});

// Emits
const emits = defineEmits([
  "apply-sorting",
  "update-project-role",
  "get-subprojects-page",
  "refresh-role",
  "get-roles-page",
  "get-skills",
  "get-cost-centers",
  "destroy-user-role",
]);

// Mixins
const { toMoney, toPercentage } = useMixin();

// Data properties
const editRoleModalVisible = ref<boolean>(false);
const deleteRoleModalVisible = ref<boolean>(false);
const addWorkExperienceModalVisible = ref<boolean>(false);
const role = ref<any>(null);
const extensionStatusColorRef = ref<{ [key: string]: any }>(extensionStatusColor);

// Class methods
const handleChange = (
  pagination: PaginationInterface,
  filters: RolesFilterInterface,
  sorter: any
): void => {
  emits("apply-sorting", {
    sort: sorter.columnKey,
    order: sorter.columnKey ? (sorter.order === "descend" ? "desc" : "asc") : undefined,
  });
};

const showEditRoleModal = (r: any): void => {
  role.value = r;
  editRoleModalVisible.value = true;
};

const showDeleteRoleModal = (r: RoleInterface): void => {
  role.value = r;
  deleteRoleModalVisible.value = true;
};

const canAddWorkExperienceToRoleUser = (r: RoleInterface): boolean => {
  return (
    r.user.id === props.profile?.id || props.acl.isAdmin || props.acl.isCr || props.acl.isFinancial
  );
};

const redirectToWorkExperiences = (workExperience: any): void => {
  window.location.href = `/dashboard/workExperiences?filters[work_experience_id]=${workExperience.id}`;
};

const showAddWorkExperienceModal = (r: RoleInterface): void => {
  if (!canAddWorkExperienceToRoleUser(r)) {
    return;
  }

  emits("get-skills");
  role.value = r;
  addWorkExperienceModalVisible.value = true;
};

const handleWorkExperienceAdded = (payload: any): void => {
  addWorkExperienceModalVisible.value = false;
  // Emit event to refresh the role data
  emits("refresh-role", role.value.id);
};
</script>

<style scoped>
.min-width-avatar {
  min-width: 32px;
}
</style>
